import * as Sentry from "@sentry/browser";
import { Button, Col, Dropdown, Menu, Row, Spin, Table, Tag, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/lib/form";
import { ColumnType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import queryString from "query-string";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ExportData from "../components/ExportData";
import ImportData from "../components/ImportData";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import entitlements from "../config/entitlements";
import {
  Company,
  FileOption,
  FormFieldConfig,
  Product,
  QprReport,
  RowSelection,
  SelectOptionsInterface,
  Subscription,
  searchQueryParams,
  APIError,
  ContactLevel,
} from "../helpers/shared-interfaces";
import {
  loadSavedFilters,
  request,
  responseHandler,
  mapAPIErrorToFormError,
  generateDisplayErrorMessage,
  showTotalPagination,
} from "../helpers/utilities";
import { CREATE_QPR_REPORT, INITIATE_QPR_PROCESS, GET_FILE_OPTIONS_FOR_COMPANY, SEARCH_COMPANY_LIST } from "../gql/companies";
import { SEARCH_PRODUCTS } from "../gql/products";
import { GET_CONTACT_LEVELS } from "../gql/templates";
import { DEBOUNCE_TIME } from "../helpers/constants";
import { extractPageFromQuery } from "../components/TableFilters";



interface State {
  isLoading: boolean;
  companies: Company[];
  companiesCount: number;
  page: number;
  pageSize: number;
  q: string;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
  selectedIdsToExport: React.Key[];
  showQprModal: boolean;
  generatedQprUrl?: string;
  isQprLoading: boolean;
  qprCompanies: Company[];
  qprSearch: string;
  searchType: SearchType;
  companyProducts: Product[];
  showInitiateQprModal: boolean;
  isInitiateQprLoading: boolean;
  initiateQprReference: RefObject<FormInstance>;
  initiateQprProducts: Product[];
  initiateQprProductSearchTerm: string;
  notifyContactPeople: boolean;
  contactLevels: ContactLevel[];
  fileOptions: FileOption[];
}

enum SearchType {
  main,
  qpr,
}


function getCompanyColumns<T extends Company = Company>(
  requests: Company[],
): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "PetraID",
      dataIndex: "petraId",
      key: "petraId",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "company",
      key: "company",
      render(text: string, record: Company): JSX.Element {
        return <span>{record.name}</span>;
      },
    },
    {
      title: "Email",
      dataIndex: "companyEmails",
      key: "companyEmails",
      width: "20%",
      render(text: string, record: Company): JSX.Element {
        let email: string = record?.companyEmails?.[0]?.email?.address ?? "";
        return (
          <span>
            {email ? (
              <div key="cEmail">
                <span>{email}</span>
              </div>
            ) : (
              <Tag color="red" key="cEmailNotAssigned">
                No Email
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "companyPhones",
      key: "companyPhones",
      width: "15%",
      render(text: string, record: Company): JSX.Element {
        let phone: string = record?.companyPhones?.[0]?.phone?.number ?? "";
        return (
          <span>
            {phone ? (
              <div key="cPhone">
                <span>{phone}</span>
              </div>
            ) : (
              <Tag color="red" key="cPhoneNotAssigned">
                No Mobile Number
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: "15%",
      render(text: string, record: Company): JSX.Element {
        let contactPerson: string | undefined = undefined;
        contactPerson =
          record.contactPersonValue && record.contactPersonValue.split(",")[0];
        return (
          <span>
            {contactPerson ? (
              <div key="cPhone">
                <span>{contactPerson}</span>
              </div>
            ) : (
              <Tag color="red" key="contactPersonNotAvailable">
                No Contact Person
              </Tag>
            )}
          </span>
        );
      },
    },
  ];
}

export default class Companies extends React.Component<
  RouteComponentProps,
  State
> {
  reference: RefObject<FormInstance>;
  qprReference: RefObject<FormInstance>;
  initiateQprReference: RefObject<FormInstance>;
  importDataRef: RefObject<HTMLDivElement>;
  exportDataRef: RefObject<HTMLDivElement>;

  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      isLoading: true,
      companies: [],
      companiesCount: 0,
      showAdvancedSearchModal: false,
      page: 1,
      pageSize: 25,
      q: "",
      selectedFieldsToDisplay: [],
      selectedIdsToExport: [],
      showQprModal: false,
      generatedQprUrl: undefined,
      isQprLoading: false,
      qprCompanies: [],
      qprSearch: "",
      searchType: SearchType.main,
      companyProducts: [],
      showInitiateQprModal: false,
      isInitiateQprLoading: false,
      initiateQprReference: React.createRef<FormInstance>(),
      initiateQprProducts: [],
      initiateQprProductSearchTerm: "",
      notifyContactPeople: false,
      contactLevels: [],
      fileOptions: [],
    };
    this.reference = React.createRef<FormInstance>();
    this.qprReference = React.createRef<FormInstance>();
    this.initiateQprReference = React.createRef<FormInstance>();
    this.importDataRef = React.createRef<HTMLDivElement>();
    this.exportDataRef = React.createRef<HTMLDivElement>();
    this.nextPage = this.nextPage.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.toggleInitiateQprModal = this.toggleInitiateQprModal.bind(this);
    this.initiateQpr = this.initiateQpr.bind(this);
    this.handleInitiateQprProductSearch = this.handleInitiateQprProductSearch.bind(this);
    this.triggerInitiateQprProductSearch = this.triggerInitiateQprProductSearch.bind(this);
    this.changeNotifyContactPeople = this.changeNotifyContactPeople.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.mergeWithLocalState();
    if (loadSavedFilters("company").length < 1) {
      await this.triggerImmediateSearch();
    }
    await this.getFileOptions();
    await this.getContactLevels();
  }

  getFileOptions = async (): Promise<void> => {
    request({
      query: GET_FILE_OPTIONS_FOR_COMPANY,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          fileOptionsForCompany: FileOption[];
        };
      }>): void => {
        this.setState({
          fileOptions: data.data.fileOptionsForCompany,
        });
      },
    );
  };

  getContactLevels = async (): Promise<void> => {
    request({
      query: GET_CONTACT_LEVELS,
      variables: {
        page: 1,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          contactLevels: ContactLevel[];
        };
      }>): void => {
        this.setState({
          contactLevels: data.data.contactLevels,
        });
      },
    );
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState(
      { isLoading: true, page, pageSize: pageSize },
      async () => {
        if (this.state.selectedFieldsToDisplay.length < 1) {
          await this.triggerImmediateSearch();
        }
        this.setState({ isLoading: false });
      },
    );
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    this.setState({ q: event.target.value });
    await this.triggerDelayedSearch();
  };

  handleQprCompanySearch = async (search: string) => {
    this.setState({ qprSearch: search });
    await this.triggerDelayedSearch();
  };

  companySelect = async (id: string) => {
    const selectedCompanySubscriptions = this.state.qprCompanies.find(
      (item) => item.id === id,
    )?.companySubscriptions;
    const companyProducts = this.getCompanyProducts(
      selectedCompanySubscriptions ?? [],
    );

    this.setState({
      companyProducts,
    });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = async (): Promise<void> => {
    const { q, qprSearch, searchType, page, pageSize } = this.state;
    interface Response {
      count: number;
      results: Company[];
    }

    this.setState({ isLoading: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_COMPANY_LIST,
        variables: {
          q: searchType === SearchType.main ? q : qprSearch,
          page: searchType === SearchType.main ? page : 1,
          numberPerPage: pageSize,
        },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "companySearch",
      );

      if (handled) {
        message.error("Failed To Load Companies");
      } else {
        await this.syncBrowserQueryString();
        if (searchType === SearchType.main) {
          this.setState({
            companies: mutation.results,
            companiesCount: mutation.count,
          });
        } else {
          this.setState({
            qprCompanies: mutation.results,
          });
        }
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  getCompanyProducts = (subscriptions: Subscription[]) => {
    return subscriptions?.map((item) => item.product) ?? [];
  };

  extractQueryParamsFromUrl = (): searchQueryParams => {
    const parsed = queryString.parse(window.location.search);
    return JSON.parse(JSON.stringify(parsed)) as searchQueryParams;
  };

  mergeWithLocalState = async (): Promise<void> => {
    const parsed = this.extractQueryParamsFromUrl();
    let q = "";
    let page = 1;

    if (parsed.q) {
      q = parsed.q;
    }

    if (parsed.page) {
      page = extractPageFromQuery(parsed.page);
    }

    this.setState({
      q,
      page,
    });
  };

  syncBrowserQueryString = (): void => {
    const { q, page } = this.state;
    const queryStrings = [];

    if (q) {
      queryStrings.push(`q=${q}`);
    }

    queryStrings.push(`page=${page}`);

    const queryString = `?${queryStrings.join("&")}`;

    this.props.history.replace(queryString);
  };

  toggleQprModal = (): void => {
    const prevState = this.state.showQprModal;
    this.setState({
      showQprModal: !prevState,
      searchType: !prevState ? SearchType.qpr : SearchType.main,
    });
  };

  generateQpr = async () => {
    if (!this.qprReference.current) return;
    interface Response {
      qprReport: QprReport;
    }

    const fieldsToBeValidated: string[] = [
      "companyId",
      "productId",
      "quarter",
      "year",
      "qprType",
      "contactLevels",
      "notifyContactPeople"
    ];

    let data;
    data = await this.qprReference.current.validateFields(
      fieldsToBeValidated,
    );

    try {
      const response: AxiosResponse<Response> = await request({
        query: CREATE_QPR_REPORT,
        variables: {
          ...data,
          notifyContactPeople: this.state.notifyContactPeople,
          contactLevels: data.contactLevels && data.contactLevels.length > 0 ? data.contactLevels : [],
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "generateQprReport",
      );

      this.setState({ isQprLoading: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        message.success("QPR Report Generation process started successfully");
      }
    } catch (error) {
      message.error(`Failed to start QPR Generation process - ${generateDisplayErrorMessage(error)}`);
      this.setState({ isQprLoading: false });
    }
  };


  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      companies: data.results as Company[],
      selectedFieldsToDisplay: fields,
      companiesCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );

    this.setState(
      {
        removedField: field,
        selectedFieldsToDisplay: fields,
      },
      () => {
        localStorage.setItem(
          "company",
          JSON.stringify(this.state.selectedFieldsToDisplay),
        );
      },
    );

    if (fields.length === 0) this.triggerDelayedSearch();
  };

  onSelectChange(newSelectedIdsToExport: React.Key[]) {
    this.setState({
      selectedIdsToExport: Array.from(newSelectedIdsToExport).map(
        (item) => item,
      ),
    });
  }

  toggleInitiateQprModal = (): void => {
    const prevState = this.state.showInitiateQprModal;
    this.setState({ showInitiateQprModal: !prevState });
  };

  initiateQpr = async (): Promise<void> => {
    if (!this.initiateQprReference.current) return;
    interface Response {
      result: boolean;
      errors: APIError[];
    }

    const fieldsToBeValidated = ["productId", "quarter", "year", "qprType", "contactLevels", "companySegment", "notifyContactPeople"];

    try {
      const data = await this.initiateQprReference.current.validateFields(fieldsToBeValidated);

      const response: AxiosResponse<Response> = await request({
        query: INITIATE_QPR_PROCESS,
        variables: {
          productId: data.productId,
          quarter: data.quarter,
          year: data.year,
          qprType: data.qprType,
          notifyContactPeople: this.state.notifyContactPeople,
          contactLevels: data.contactLevels && data.contactLevels.length > 0 ? data.contactLevels : [],
          companySegment: data.companySegment || "",
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "initiateQprProcess",
      );

      this.setState({ isInitiateQprLoading: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.initiateQprReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (mutation) {
        message.success("QPR generation initiated successfully");
        this.toggleInitiateQprModal();
      }
    } catch (error) {
      this.setState({ isInitiateQprLoading: false });
      message.error(`Failed to initiate QPR generation - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  getInitiateQprProducts = async (): Promise<void> => {
    request({
      query: SEARCH_PRODUCTS,
      variables: {
        page: 1,
        q: this.state.initiateQprProductSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        initiateQprProducts: data.products.results,
        isLoading: false,
      });
    });
  };

  handleInitiateQprProductSearch = async (searchTerm: string): Promise<void> => {
    this.setState({ initiateQprProductSearchTerm: searchTerm }, () => this.triggerInitiateQprProductSearch());
  };

  triggerInitiateQprProductSearch = debounce(async (): Promise<void> => {
    return this.getInitiateQprProducts();
  }, DEBOUNCE_TIME);

  changeNotifyContactPeople = async (checkedValue: boolean) => {
    this.setState({
      notifyContactPeople: checkedValue,
    });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      companies,
      companiesCount,
      page,
      pageSize,
      q,
      selectedFieldsToDisplay,
      removedField,
      selectedIdsToExport,
    } = this.state;

    const resultsWithKeys: Company[] = companies.map(
      (result: Company): Company => {
        return {
          ...result,
          key: result.id,
        };
      },
    );

    // rowSelection object indicates the need for row selection
    const rowSelection: RowSelection = {
      selectedIdsToExport,
      type: "checkbox",
      onChange: this.onSelectChange,
    };


    const quarters: SelectOptionsInterface[] = [
      {
        id: "Q1",
        name: "Q1",
      },
      {
        id: "Q2",
        name: "Q2",
      },
      {
        id: "Q3",
        name: "Q3",
      },
      {
        id: "Q4",
        name: "Q4",
      },
    ];

    let years: SelectOptionsInterface[] = [];

    let currYear = new Date().getFullYear();

    for (let year: number = 2012; year <= currYear; year++) {
      years.push({
        id: year.toString(),
        name: year.toString(),
      });
    }

    const qprTypeOptions = [
      { id: "one_pager", value: "one_pager", name: "One Pager" },
      { id: "bulk", value: "bulk", name: "Bulk" }
    ];

    const companySegmentOptions: SelectOptionsInterface[] = [
      { id: "strategic", value: "strategic", name: "Strategic" },
      { id: "niche", value: "niche", name: "Niche" },
      { id: "growth", value: "growth", name: "Growth" },
      { id: "maintenance", value: "maintenance", name: "Maintenance" },
    ];

    const contactLevelOptions: SelectOptionsInterface[] = this.state.contactLevels.map((cl) => ({
      id: cl.level,
      name: cl.name,
      value: cl.level
    }));


    const modifiedInitiateQprProducts: SelectOptionsInterface[] = this.state.initiateQprProducts.map((p) => ({
      name: p.name,
      value: p.id,
      id: p.id
    }));

    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="company"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          pageSize={pageSize}
          page={page}
        />
        <Card>
            <div className="company-container">

              <PageToolbar
                searchPlaceholder="Search by PetraID, Email, Phone, Name, StaffId, Contact Person ..."
                searchValue={q}
                onSearchChange={this.handleSearchInput}
                totalCount={companiesCount}
                currentDataCount={companies.length}
                page={page}
                pageSize={pageSize}
                selectedCount={selectedIdsToExport.length}
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                primaryAction={
                  <>
                    <div ref={this.importDataRef} style={{ display: "none" }}>
                      <EntitledSection entitlement={entitlements.BulkUploads.list}>
                        <ImportData
                          model="Company"
                          title="Import Company Data"
                          buttonText="Import Company Data"
                          fileOptions={this.state.fileOptions}
                        />
                      </EntitledSection>
                    </div>
                    <div ref={this.exportDataRef} style={{ display: "none" }}>
                      <ExportData
                        model={"company"}
                        dataArray={companies}
                        size={"large"}
                        customIds={selectedIdsToExport}
                      />
                    </div>
                    <Dropdown
                      trigger={["click"]}
                      overlay={
                        <Menu>
                          <Menu.Item
                            key="import"
                            onClick={() =>
                              this.importDataRef.current
                                ?.querySelector("button")
                                ?.click()
                            }
                          >
                            Import Company Data
                          </Menu.Item>
                          <Menu.Item
                            key="export"
                            onClick={() =>
                              this.exportDataRef.current
                                ?.querySelector("button")
                                ?.click()
                            }
                          >
                            Export Data
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <Button type="primary" size="large">
                        Import / Export <DownOutlined />
                      </Button>
                    </Dropdown>
                  </>
                }
                actions={[
                  <EntitledSection entitlement={entitlements.Qpr.generate} key="qpr-actions">
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item key="single" onClick={this.toggleQprModal}>
                            Single QPR
                          </Menu.Item>
                          <Menu.Item key="bulk" onClick={this.toggleInitiateQprModal}>
                            Bulk QPR
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                    >
                      <Button type="primary" size="large">
                        Generate QPR <DownOutlined />
                      </Button>
                    </Dropdown>
                  </EntitledSection>,
                ]}
                modelName="companies"
              />

              <Row>
                <Col span={24}>
                    <Table
                      loading={isLoading}
                      scroll={{ x: 400 }}
                      columns={getCompanyColumns(companies)}
                      dataSource={resultsWithKeys}
                      onRow={(record) => ({
                        onClick: () => {
                          this.props.history.push({
                            pathname: `/companies/${record.id}`,
                            state: { type: "company", requests: companies },
                          });
                        },
                        style: { cursor: 'pointer' }
                      })}
                      pagination={{
                        current: page,
                        defaultPageSize: pageSize,
                        total: companiesCount,
                        onChange: this.nextPage,
                        showTotal: showTotalPagination,
                      }}
                      rowSelection={rowSelection}
                    />
                </Col>
              </Row>
              {this.state.showQprModal &&
                ModalCreator({
                  title: "Generate Single Quarterly Performance Report",
                  okType: "primary",
                  okText: "Generate",
                  isLoading: this.state.isQprLoading,
                  showModal: this.state.showQprModal,
                  showFooter: true,
                  onOkay: this.generateQpr,
                  toggleModal: this.toggleQprModal,
                  inscription: QprUI({
                    reference: this.qprReference,
                    quarters: quarters,
                    years: years,
                    url: this.state.generatedQprUrl,
                    qprCompanies: this.state.qprCompanies,
                    handleSearch: this.handleQprCompanySearch,
                    companyProducts: this.state.companyProducts,
                    companySelect: this.companySelect,
                    qprTypes: qprTypeOptions,
                    contactLevels: contactLevelOptions,
                    changeNotifyContactPeople: this.changeNotifyContactPeople,
                    notifyContactPeopleStatus: this.state.notifyContactPeople,
                  }),
                })}
              {this.state.showInitiateQprModal &&
                ModalCreator({
                  title: "Initiate QPR Generation",
                  okType: "primary",
                  okText: "Initiate",
                  isLoading: this.state.isInitiateQprLoading,
                  showModal: this.state.showInitiateQprModal,
                  showFooter: true,
                  onOkay: this.initiateQpr,
                  toggleModal: this.toggleInitiateQprModal,
                  inscription: (
                    <Spin spinning={false}>
                      <Row>
                        <Col span={24} style={{ marginBottom: "20px" }}>
                          <div style={{ marginTop: "10px" }}>
                            <BaseForm
                              layout="vertical"
                              reference={this.initiateQprReference}
                              fields={[
                                {
                                  id: "productId",
                                  name: "productId",
                                  inputType: "search",
                                  required: true,
                                  rule: "string",
                                  selectOptions: modifiedInitiateQprProducts,
                                  changeHandler: this.handleInitiateQprProductSearch,
                                  placeholder: "Select Product",
                                  label: "Product",
                                  elementStyle: { width: "100%" },
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "quarter",
                                  name: "quarter",
                                  rule: "string",
                                  inputType: "select",
                                  required: true,
                                  label: "Quarter",
                                  placeholder: "Select Quarter",
                                  selectOptions: quarters,
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "year",
                                  name: "year",
                                  rule: "string",
                                  inputType: "select",
                                  required: true,
                                  label: "Year",
                                  placeholder: "Select Year",
                                  selectOptions: years,
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "qprType",
                                  name: "qprType",
                                  rule: "string",
                                  inputType: "select",
                                  required: true,
                                  label: "QPR Type",
                                  placeholder: "Select QPR Type",
                                  selectOptions: qprTypeOptions,
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "contactLevels",
                                  name: "contactLevels",
                                  inputType: "multipleSelect",
                                  required: false,
                                  label: "Contact Levels",
                                  placeholder: "Select Contact Levels",
                                  selectOptions: contactLevelOptions,
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "companySegment",
                                  name: "companySegment",
                                  rule: "string",
                                  inputType: "select",
                                  required: false,
                                  label: "Company Segment",
                                  placeholder: "Select Company Segment",
                                  selectOptions: companySegmentOptions,
                                  colConfig: {
                                    span: 24,
                                  },
                                },
                                {
                                  id: "notifyContactPeople",
                                  name: "notifyContactPeople",
                                  inputType: "checkbox",
                                  checkboxCheckedValue: this.state.notifyContactPeople,
                                  checkboxChangeHandler: (e) => this.changeNotifyContactPeople(e.target.checked),
                                  rule: "boolean",
                                  required: false,
                                  label: "Notify Contact People",
                                  colConfig: {
                                    span: 24,
                                  },
                                }
                              ]}
                              fieldsToHide={[]}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Spin>
                  ),
                })}
            </div>
          </Card>
      </>
    );
  }
}


export const QprUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  quarters: SelectOptionsInterface[];
  years: SelectOptionsInterface[];
  qprTypes: SelectOptionsInterface[];
  url: string | undefined;
  qprCompanies: Company[];
  companyProducts: Product[];
  handleSearch: (search: string) => void;
  companySelect: (id: string) => void;
  contactLevels: SelectOptionsInterface[];
  changeNotifyContactPeople: (checked: boolean) => void;
  notifyContactPeopleStatus: boolean;
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "companyId",
      name: "companyId",
      inputType: "search",
      required: true,
      rule: "string",
      selectOptions: props.qprCompanies,
      changeHandler: props.handleSearch,
      selectHandler: props.companySelect,
      placeholder: "Select Company",
      label: "Company",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "productId",
      name: "productId",
      inputType: "select",
      required: true,
      rule: "string",
      selectOptions: props.companyProducts,
      placeholder: "Select Product",
      label: "Product",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "quarter",
      name: "quarter",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Quarter",
      placeholder: "Select Quarter",
      selectOptions: props.quarters,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "year",
      name: "year",
      rule: "string",
      inputType: "select",
      required: true,
      label: "Year",
      placeholder: "Select Year",
      selectOptions: props.years,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "qprType",
      name: "qprType",
      rule: "string",
      inputType: "select",
      required: true,
      label: "QPR Type",
      placeholder: "Select Type",
      selectOptions: props.qprTypes,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "contactLevels",
      name: "contactLevels",
      inputType: "multipleSelect",
      required: false,
      label: "Contact Levels",
      placeholder: "Select Contact Levels",
      selectOptions: props.contactLevels,
      colConfig: {
        span: 24,
      },
    },
    {
      id: "notifyContactPeople",
      name: "notifyContactPeople",
      inputType: "checkbox",
      rule: "boolean",
      required: false,
      checkboxCheckedValue: props.notifyContactPeopleStatus,
      checkboxChangeHandler: (e) => props.changeNotifyContactPeople(e.target.checked),
      label: "Notify Contact People",
      colConfig: {
        span: 24,
      },
    }
  ];

  return (
    <>
      <Spin spinning={false}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fields={fields}
                fieldsToHide={[]}
              ></BaseForm>

              {props.url && (
                <p>
                  <a href={props.url} target="_blank" rel="noopener noreferrer">
                    Download statement
                  </a>
                </p>
              )}
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};
